/* eslint-disable no-nested-ternary */

import { useMemo, useState, useEffect, useRef } from 'react';
import ReactApexChart from 'react-apexcharts';
import moment from 'moment';
import { ApexOptions } from 'apexcharts';
import _merge from 'lodash/merge';
import { Stack, Typography } from '@mui/material';
import { FullscreenRounded } from '@mui/icons-material';
import { SelectGranularity } from '../../customers/db-services-detailed-view/db-monitoring/components/SelectGranularity';
import { SelectViewAs } from '../../customers/db-services-detailed-view/db-monitoring/components/SelectViewAs';
import {
  countDecimals,
  getYAxisRange,
} from '../../customers/db-services-detailed-view/db-monitoring/components/ZoomableLineChart';
import {
  ChartProps,
  AdaptedMetricType,
} from '../../customers/db-services-detailed-view/db-monitoring/monitoringTypes';
import { DF_HUMAN } from 'helpers/dateFormats';
import OverflowTooltip from 'common/overflow-tooltip/lib/components/OverflowTooltip';
import {
  monitoringCustomStyles,
  fontStyles,
  styles,
} from '../../customers/db-services-detailed-view/db-monitoring/monitoringCustomStyles';
import { getGraphColors } from './getGraphColors';
import { isGranularitySupported } from '../../customers/db-services-detailed-view/db-monitoring/utils/granularityUtils';
import {
  ViewAsEnum,
  DEFAULT_VIEW_AS,
} from '../../customers/db-services-detailed-view/db-monitoring/constants';

export const BasicAreaCharts = (props: ChartProps) => {
  const {
    chart,
    chartOptions = {},
    customColor,
    showFullScreenToolbar = true,
    showLatestValue = true,
    showUnit = true,
    granularityControl,
    startTime,
    endTime,
    fullScreenChart: fullScreenChartProp,
    setFullScreenChart: setFullScreenChartProp,
  } = props;

  // Extract values from wrapped props
  const granularity = granularityControl?.granularity;
  const setGranularity = granularityControl?.setGranularity;

  const graphColours = getGraphColors();
  const { title, dataSets, minVal, maxVal, fill, unit } = chart || {
    title: 'This is a Placeholder chart',
    dataSets: [
      {
        labelSet: [1, 2, 3],
        dataSet: [4, 5, 6],
        minVal: 4,
        maxVal: 6,
        displayName: 'Placeholder Line',
      },
    ],
    minVal: 1,
    maxVal: 10,
    unit: 'Unit',
  };

  const [visibleSeries, setVisibleSeries] = useState<boolean[]>(
    dataSets?.map(() => true),
  );

  const updateVisibleSeries = (index) => {
    setVisibleSeries((visibleSeries) => {
      const newVisibleSeries = [...visibleSeries];
      newVisibleSeries[index] = !visibleSeries[index];
      return newVisibleSeries;
    });
  };

  // ViewAs state - separate for each chart (resets when chart changes)
  const [viewAs, setViewAs] = useState<ViewAsEnum>(DEFAULT_VIEW_AS);

  // Reset ViewAs to default when chart changes
  useEffect(() => {
    setViewAs(DEFAULT_VIEW_AS);
  }, [chart?.id]);

  // Filter chart dataSets based on selected ViewAs (only for fullscreen view)
  const filteredChart = useMemo(() => {
    // Only filter in fullscreen view (!showFullScreenToolbar means we're in fullscreen)
    if (
      !chart?.dataSets ||
      !Array.isArray(chart.dataSets) ||
      showFullScreenToolbar
    ) {
      return chart;
    }

    const filteredDataSets = chart.dataSets.filter((dataset) => {
      // Include datasets with null/empty aggregationType (like System Load Average)
      if (
        !dataset?.aggregationType ||
        (typeof dataset.aggregationType === 'string' &&
          dataset.aggregationType.trim() === '')
      ) {
        return true;
      }

      // Filter by selected ViewAs
      return (
        dataset.aggregationType.toLocaleLowerCase() === viewAs.toLowerCase()
      );
    });

    return {
      ...chart,
      dataSets: filteredDataSets,
    };
  }, [viewAs, showFullScreenToolbar]);

  // Use filtered chart dataSets for series calculation (only in fullscreen view)
  const chartDataSets = showFullScreenToolbar
    ? dataSets
    : filteredChart?.dataSets || dataSets;

  const series: ApexAxisChartSeries = useMemo(
    () =>
      chartDataSets?.map((dataset) => {
        const newData = dataset?.labelSet?.map?.((value, index) => ({
          x: value,
          y: dataset?.dataSet[index],
        }));

        return { name: dataset?.displayName, data: newData };
      }) || [],
    [chartDataSets],
  );

  const yAxisRange = useMemo(
    () => getYAxisRange(visibleSeries, series, chartOptions),
    [visibleSeries, series, chartOptions],
  );

  let dpCount = 0;
  dataSets?.forEach((dataset) => {
    dpCount += dataset?.labelSet?.length;
  });

  const isPercentageUnit =
    unit?.toLowerCase() === 'percent' || unit?.toLowerCase() === '%';

  const yMin = yAxisRange.min;
  const yMax = isPercentageUnit
    ? 100
    : yAxisRange.max && !!dpCount
    ? yAxisRange.max
    : 5;

  // Use shared fullscreen state if provided, otherwise use local state as fallback
  const [localFullScreenChart, setLocalFullScreenChart] =
    useState<AdaptedMetricType | null>(null);
  const fullScreenChart =
    fullScreenChartProp !== undefined
      ? fullScreenChartProp
      : localFullScreenChart;
  const setFullScreenChart = setFullScreenChartProp || setLocalFullScreenChart;

  // Use ref to track which chart ID should be open (prevents wrong chart opening)
  const openChartIdRef = useRef<string | null>(null);

  const memoChart = useMemo(() => {
    const options: ApexOptions = {
      chart: {
        type: 'area',
        stacked: false,
        width: 640,
        height: 400,
        zoom: {
          type: 'x',
          enabled: true,
          autoScaleYaxis: true,
        },
        events: {
          legendClick: (chart: any, seriesIndex: number, options: any) => {
            updateVisibleSeries(seriesIndex);
          },
        },
        toolbar: {
          show: true,
          autoSelected: 'zoom',
          offsetY: -85,
          tools: {
            download: true,
            selection: false,
            zoom: true,
            zoomin: false,
            zoomout: false,
            pan: true,
            reset: true,
          },
          export: {
            csv: {
              filename: undefined,
              columnDelimiter: ',',
              headerCategory: 'category',
              headerValue: 'value',
              dateFormatter(timestamp: number) {
                return new Date(timestamp).toDateString();
              },
            },
          },
        },
        animations: {
          enabled: false,
          easing: 'easeinout',
          speed: 0,
          animateGradually: {
            enabled: false,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 300,
          },
        },
      },
      stroke: {
        show: true,
        curve: 'straight',
        width: 1,
      },
      markers: {
        size: 0,
      },
      colors: customColor ?? graphColours,
      dataLabels: {
        enabled: false,
      },

      fill: {
        type: 'solid',
        gradient: {
          shade: 'light',
          shadeIntensity: 0,
          opacityFrom: 0.3,
          opacityTo: 0.3,
        },
      },
      yaxis: {
        forceNiceScale: !isPercentageUnit,
        showAlways: true,
        showForNullSeries: true,
        tickAmount: 5,
        labels: {
          show: true,
          formatter: (val) => {
            const formatVal = (labelText) => {
              if (yMax < 1) {
                return val.toFixed(Math.min(countDecimals(val), 3));
              }
              return val.toFixed(1);
            };
            return Number.isInteger(val) ? val.toString() : formatVal(val);
          },
          style: {
            colors: '#979797',
          },
        },
        // seriesName: 'x',
        axisTicks: {
          show: true,
        },
        axisBorder: {
          show: true,
        },
        crosshairs: {
          show: true,
        },
      },

      xaxis: {
        type: 'datetime',
        labels: {
          show: true,

          hideOverlappingLabels: true,
          style: {
            colors: '#979797',
          },
          datetimeUTC: false,
          datetimeFormatter: {
            year: 'yyyy',
            month: "MMM 'yy",
            day: 'dd MMM',
            hour: 'HH:mm',
            minute: 'HH:mm',
          },
        },
        tickAmount: 5,
      },

      legend: {
        showForSingleSeries: true,
        onItemClick: {
          toggleDataSeries: true,
        },
        position: 'bottom',
        horizontalAlign: 'left',
        markers: {
          offsetX: -5,
          offsetY: 2,
        },
      },

      tooltip: {
        shared: true,
        followCursor: true,
        x: {
          formatter: (value) => moment(value).format(DF_HUMAN),
        },
        y: {
          formatter: (val) => val?.toFixed?.(3),
        },
      },
    };

    const mergedOptions = _merge(options, chartOptions);

    return (
      <Stack sx={{ flexDirection: 'row', flex: '1 1 auto' }}>
        {showUnit && (
          <Stack sx={monitoringCustomStyles.BasicAreaCharts.unitContainer}>
            <Typography
              sx={monitoringCustomStyles.BasicAreaCharts.unitTextStyle}
            >
              {unit}
            </Typography>
          </Stack>
        )}
        <Stack sx={{ flex: '1 1 auto' }} justifyContent={'space-between'}>
          <Stack
            sx={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography sx={monitoringCustomStyles.BasicAreaCharts.title}>
              {title}
            </Typography>
            {!showFullScreenToolbar &&
              isGranularitySupported(chart) &&
              granularity &&
              setGranularity &&
              startTime &&
              endTime && (
                <Stack
                  sx={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <SelectViewAs viewAs={viewAs} setViewAs={setViewAs} />
                  <SelectGranularity
                    granularity={granularity}
                    setGranularity={setGranularity}
                    startTime={startTime}
                    endTime={endTime}
                  />
                </Stack>
              )}
          </Stack>
          {showLatestValue && series?.length < 4 && (
            <Stack sx={monitoringCustomStyles.BasicAreaCharts.latestValues}>
              {series?.map((legend) => (
                <Stack sx={{ mr: 2, width: '120px' }}>
                  <OverflowTooltip
                    text={`${legend?.name} (latest)`}
                    variant="body"
                  />
                  <Typography
                    sx={{
                      ...fontStyles.fs18_fw600,
                      color: styles.color.textBoxLabel,
                    }}
                  >
                    {(
                      legend?.data?.[legend?.data?.length - 1] as any
                    )?.y?.toFixed(2)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
          <ReactApexChart
            dir="ltr"
            options={mergedOptions}
            series={series}
            type="area"
            height={chartOptions?.chart?.height || '350'}
            className="apex-charts"
          />
        </Stack>
      </Stack>
    );
  }, [
    series,
    minVal,
    maxVal,
    unit,
    title,
    chartOptions,
    chartOptions?.xaxis?.min,
  ]);

  // Sync fullscreen chart data when chart prop updates (but only if this chart is open)
  useEffect(() => {
    // Only update if this specific chart is the one that should be open
    // Check both the ref and the fullScreenChart ID to ensure we're updating the right chart
    if (
      fullScreenChart &&
      chart?.id &&
      openChartIdRef.current === chart.id &&
      chart.id === fullScreenChart.id &&
      setFullScreenChart
    ) {
      // Only update if the chart data actually changed
      if (chart.id === fullScreenChart.id) {
        setFullScreenChart(chart);
      }
    }
  }, [chart, fullScreenChart, setFullScreenChart]);

  // Clear ref when modal closes (only clear if this chart's ref was set)
  useEffect(() => {
    if (!fullScreenChart && openChartIdRef.current === chart?.id) {
      openChartIdRef.current = null;
    }
  }, [fullScreenChart, chart?.id]);

  return (
    <>
      {showFullScreenToolbar && (
        <Stack
          sx={monitoringCustomStyles.BasicAreaCharts.showFullScreenChartIcon}
          onClick={(ev) => {
            ev?.stopPropagation();
            // Store the chart ID in ref to ensure we open the correct chart
            if (chart?.id) {
              openChartIdRef.current = chart.id;
              setFullScreenChart(chart);
            }
          }}
        >
          <FullscreenRounded
            sx={monitoringCustomStyles.BasicAreaCharts.fullScreenRounded}
          />
        </Stack>
      )}
      {memoChart}
    </>
  );
};
